import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { globalRealtimeManager } from '@/services/globalRealtimeManager';

interface UnreadMessagesContextType {
  unreadCount: number;
  loading: boolean;
  forceSync: () => void;
  getConnectionHealth: () => Record<string, any>;
  decrementUnreadCount: (count: number) => void;
}

const UnreadMessagesContext = createContext<UnreadMessagesContextType>({
  unreadCount: 0,
  loading: true,
  forceSync: () => {},
  getConnectionHealth: () => ({}),
  decrementUnreadCount: () => {},
});

export const useUnreadMessagesContext = () => useContext(UnreadMessagesContext);

export const UnreadMessagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isUpdatingRef = useRef(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const pollingIntervalRef = useRef<NodeJS.Timeout>();

  const loadUnreadCount = async (skipIfUpdating = true) => {
    if (!user) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    // Skip if we're in the middle of a manual update to prevent race conditions
    if (skipIfUpdating && isUpdatingRef.current) {
      console.log(`⏸️ [CleanContext] Skipping loadUnreadCount - manual update in progress`);
      return;
    }

    try {
      const { data: messages, error } = await supabase
        .from('messages')
        .select('id')
        .eq('receiver_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      const count = messages?.length || 0;
      console.log(`📨 [CleanContext] Unread messages: ${count} (updating: ${isUpdatingRef.current})`);
      
      // Only update if we're not in a manual update
      if (!isUpdatingRef.current) {
        setUnreadCount(count);
      }
    } catch (error) {
      console.error(`❌ [CleanContext] Error loading unread count:`, error);
      if (!isUpdatingRef.current) {
        setUnreadCount(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // Start polling as backup when realtime fails
  const startPolling = () => {
    if (pollingIntervalRef.current) return;
    
    console.log(`🔄 [CleanContext] Starting polling backup`);
    pollingIntervalRef.current = setInterval(() => {
      const health = globalRealtimeManager.getConnectionHealth();
      const channelId = `messages-${user?.id}`;
      const isRealtimeHealthy = health[channelId]?.isHealthy;
      
      if (!isRealtimeHealthy) {
        console.log(`📊 [CleanContext] Realtime unhealthy, polling for updates`);
        loadUnreadCount();
      }
    }, 10000); // Poll every 10 seconds when realtime is down
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = undefined;
      console.log(`⏹️ [CleanContext] Stopped polling backup`);
    }
  };

  const forceSync = () => {
    if (user) {
      console.log(`🔄 [CleanContext] Force sync triggered for user: ${user.id}`);
      globalRealtimeManager.forceSync(user.id);
      setTimeout(loadUnreadCount, 1000);
    }
  };

  const decrementUnreadCount = (count: number) => {
    console.log(`📉 [CleanContext] Decrementing unread count by ${count}`);
    
    // Set the updating flag to prevent race conditions
    isUpdatingRef.current = true;
    
    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    setUnreadCount(prev => {
      const newCount = Math.max(0, prev - count);
      console.log(`📊 [CleanContext] Unread count changed: ${prev} → ${newCount}`);
      return newCount;
    });
    
    // Reset the updating flag after a short delay to allow for database propagation
    debounceTimeoutRef.current = setTimeout(() => {
      isUpdatingRef.current = false;
      console.log(`🔓 [CleanContext] Manual update protection released`);
      
      // Force a fresh count after the protection period
      setTimeout(() => {
        loadUnreadCount(false); // Don't skip if updating since we just released the flag
      }, 500);
    }, 2000); // 2 second protection window
  };

  const getConnectionHealth = () => {
    return globalRealtimeManager.getConnectionHealth();
  };

  useEffect(() => {
    if (user) {
      console.log(`🎯 [CleanContext] Setting up for user: ${user.id}`);
      
      // Load initial count
      loadUnreadCount();
      
      // Start polling backup
      startPolling();
      
      // Subscribe to realtime updates through global manager
      const unsubscribe = globalRealtimeManager.subscribeToMessages(user.id, () => {
        console.log(`📨 [CleanContext] Realtime update received`);
        loadUnreadCount();
      });
      
      unsubscribeRef.current = unsubscribe;
    } else {
      setUnreadCount(0);
      setLoading(false);
      stopPolling();
    }

    return () => {
      if (unsubscribeRef.current) {
        console.log(`🧹 [CleanContext] Cleaning up subscription`);
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      // Clean up polling
      stopPolling();
      
      // Clean up debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      // Reset updating flag
      isUpdatingRef.current = false;
    };
  }, [user]);

  const value = {
    unreadCount,
    loading,
    forceSync,
    getConnectionHealth,
    decrementUnreadCount,
  };

  return (
    <UnreadMessagesContext.Provider value={value}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};